import { useI18n } from '@i18n';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Fab,
  Grid,
  GridProps,
  IconButton,
  LinearProgress,
  Stack,
  SxProps,
  Tooltip,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useVirtualizer } from '@tanstack/react-virtual';
import Dropzone from 'dropzone';
import union from 'lodash/union';
import { useSnackbar } from 'notistack';
import { forwardRef, ReactNode, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ICONS } from '@/assets/icons';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import Iconify from '@/components/Iconify';
import { SnackbarTimeouts } from '@/components/NotistackProvider';
import Scrollbar from '@/components/Scrollbar';
import { ICON_SIZE_MAP } from '@/config';
import { getFileIcon } from '@/utils/getFileIcon';
import { captureClientException } from '@/utils/posthogErrorTracking';

export type UploadingFile = {
  fileId: number;
  fileUrl: string;
};

export type FileId = {
  fileId: number;
};

export enum FileType {
  PDF = 'application/pdf',
  ANY = '*',
}

export type OnUploadSuccessParams = Dropzone.DropzoneFile & FileId;

// @types/dropzone omits these queue getters that the runtime exposes.
type DropzoneWithQueueGetters = Dropzone & {
  getAddedFiles: () => Dropzone.DropzoneFile[];
  getUploadingFiles: () => Dropzone.DropzoneFile[];
  getQueuedFiles: () => Dropzone.DropzoneFile[];
};

type DropzoneVariant = 'floating-action-button' | 'dropzone';

export type DropzoneComponentProps =
  | {
      variant: Extract<DropzoneVariant, 'floating-action-button'>;
      text: string;
      createUploadUrl: (fileName?: string, file?: Dropzone.DropzoneFile) => Promise<UploadingFile>;
      onUploadSuccess?: (file: OnUploadSuccessParams) => void;
      setUploadInProgress?: (uploadInProgress: boolean) => void;
      isDisabled?: boolean;
      sx?: SxProps;
    }
  | {
      variant: Extract<DropzoneVariant, 'dropzone'>;
      minimal?: boolean;
      fileDisplayName?: string;
      fileType?: FileType;
      title?: string;
      description: ReactNode;
      maxFiles?: number;
      softWarnAfter?: number;
      hideMaxFilesWarning?: boolean;
      hideProgressBar?: boolean;
      defaultValue?: Dropzone.DropzoneFile[];
      /**
       * Files to add to the dropzone queue as soon as it initialises (e.g. files handed in
       * from a drag-and-drop that opened the host dialog). Seeded by the component itself so
       * it cannot race the Dropzone instance not yet existing.
       */
      initialFiles?: File[];
      direction?: 'row' | 'column';
      iconSize?: keyof typeof ICON_SIZE_MAP;
      gridProps?: GridProps;
      createUploadUrl: (fileName?: string, file?: Dropzone.DropzoneFile) => Promise<UploadingFile>;
      onUploadSuccess?: (file: OnUploadSuccessParams) => void;
      setUploadInProgress?: (uploadInProgress: boolean) => void;
      isDisabled?: boolean;
      disabledMessage?: string;
      autoProcessQueue?: boolean;
      parallelUploads?: number;
      onQueueChanged?: (count: number) => void;
      onRetryFile?: (file: Dropzone.DropzoneFile) => void;
      virtualizeRows?: boolean;
      /** Visually hide the drop area (kept mounted for Dropzone) — e.g. while an upload is in flight. */
      hideDropArea?: boolean;
    };

export type DropzoneComponentRef = {
  getDropzone: () => Dropzone | null;
  setMaxedReached: (maxedReached: boolean) => void;
  processAll: (
    files?: Dropzone.DropzoneFile[],
    onFileError?: (file: Dropzone.DropzoneFile) => void,
  ) => Promise<Dropzone.DropzoneFile[]>;
};

const DropzoneComponent = forwardRef<DropzoneComponentRef, DropzoneComponentProps>((props, ref) => {
  const variant = props.variant;
  const isDropzone = variant === 'dropzone';
  const isFloatingActionButton = variant === 'floating-action-button';

  const fileDisplayName = isDropzone ? props.fileDisplayName : undefined;
  const fileType = isDropzone ? props.fileType : FileType.PDF;
  const title = isDropzone ? props.title : undefined;
  const description = isDropzone ? props.description : undefined;
  const maxFiles = isDropzone ? (props.maxFiles ?? 100) : 1;
  const softWarnAfter = isDropzone ? props.softWarnAfter : undefined;
  const defaultValue = isDropzone ? (props.defaultValue ?? []) : [];
  const initialFiles = isDropzone ? props.initialFiles : undefined;
  const hideMaxFilesWarning = isDropzone ? (props.hideMaxFilesWarning ?? false) : false;
  const hideProgressBar = isDropzone ? (props.hideProgressBar ?? false) : false;
  const direction = isDropzone ? (props.direction ?? 'row') : 'row';
  const iconSize = isDropzone ? props.iconSize : undefined;
  const gridProps = isDropzone ? props.gridProps : undefined;
  const disabledMessage = isDropzone ? props.disabledMessage : undefined;
  const minimal = isDropzone ? (props.minimal ?? false) : false;
  const autoProcessQueue = isDropzone ? (props.autoProcessQueue ?? true) : true;
  const parallelUploads = isDropzone ? (props.parallelUploads ?? 1) : 1;
  const onQueueChanged = isDropzone ? props.onQueueChanged : undefined;
  const onRetryFile = isDropzone ? props.onRetryFile : undefined;
  const virtualizeRows = isDropzone ? (props.virtualizeRows ?? false) : false;
  const hideDropArea = isDropzone ? (props.hideDropArea ?? false) : false;

  const text = isFloatingActionButton ? props.text : undefined;
  const sx = isFloatingActionButton ? props.sx : undefined;
  const createUploadUrl = props.createUploadUrl;
  const onUploadSuccess = props.onUploadSuccess;
  const setUploadInProgress = props.setUploadInProgress;
  const isDisabled = props.isDisabled;

  const { t } = useI18n();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const dropzoneNode = useRef<HTMLDivElement | null>(null);
  const floatingActionButtonNode = useRef<HTMLButtonElement | null>(null);
  const dropzone = useRef<Dropzone | null>(null);
  const [maxFilesReached, setMaxFilesReached] = useState<boolean>(false);
  const [handledFiles, setHandledFiles] = useState<Dropzone.DropzoneFile[]>(defaultValue);
  const [dropzoneInitialized, setDropzoneInitialized] = useState<boolean>(false);
  const [totalFiles, setTotalFiles] = useState(0);
  const [succeedCount, setSucceedCount] = useState(0);
  const [totalUploadProgress, setTotalUploadProgress] = useState(0);
  const [uploadActive, setUploadActive] = useState(false);
  const skippedFilesRef = useRef(0);
  const softWarnFiredRef = useRef(false);

  const removeHandledFile = useCallback(
    (file: Dropzone.DropzoneFile) => {
      if (!dropzone.current) return;
      dropzone.current.removeFile(file);
      const remaining = dropzone.current.files.length;
      setHandledFiles((prevFiles) => prevFiles.filter((f) => f !== file));
      setMaxFilesReached(remaining >= maxFiles);
      onQueueChanged?.(remaining);
    },
    [onQueueChanged, maxFiles],
  );

  const updateHandledFiles = (file: Dropzone.DropzoneFile) => {
    setHandledFiles((prevFiles) => union(prevFiles, [file]));
  };

  const updateTotalUploadProgress = useCallback(() => {
    if (dropzone.current) {
      const divisor = dropzone.current.files.length || 1;
      const totalProgress =
        dropzone.current.files.reduce((total, file) => total + (file.upload?.progress ?? 0), 0) / divisor;
      setTotalUploadProgress(totalProgress);
    }
  }, [dropzone]);

  const maybeSignalQueueDrained = useCallback(() => {
    const dz = dropzone.current as DropzoneWithQueueGetters | null;
    if (!dz) return;
    const drained =
      dz.getAddedFiles().length === 0 && dz.getUploadingFiles().length === 0 && dz.getQueuedFiles().length === 0;
    if (drained) {
      setUploadActive(false);
      setUploadInProgress?.(false);
    }
  }, [setUploadInProgress]);

  useEffect(() => {
    async function uploadFiles() {
      const node = isDropzone ? dropzoneNode : floatingActionButtonNode;

      if (!dropzoneInitialized && node.current && !dropzone.current) {
        Dropzone.autoDiscover = false;

        dropzone.current = new Dropzone(node.current, {
          method: 'PUT',
          autoProcessQueue,
          error(file, message) {
            if (!file.accepted) {
              const isFileTypeMismatch = file.type !== fileType;
              captureClientException(new Error(`Dropzone upload rejected: ${message}`), {
                source: 'dropzone_upload_failed',
                fileSize: file.size,
                fileType: file.type,
                expectedFileType: fileType,
                reason: isFileTypeMismatch ? 'file_type_mismatch' : 'upload_error',
              });

              if (isFileTypeMismatch) {
                enqueueSnackbar(
                  t('DropzoneComponent_UploadFileSnackbar-FileTypeError', {
                    fileName: file.name,
                    fileType: fileType,
                  }),
                  { variant: 'error', autoHideDuration: SnackbarTimeouts.Error },
                );
              } else {
                enqueueSnackbar(
                  t('DropzoneComponent_UploadFileSnackbar-Error', {
                    fileName: file.name,
                    message: message as string,
                  }),
                  {
                    variant: 'error',
                    autoHideDuration: SnackbarTimeouts.Error,
                  },
                );
              }

              // If the file is not accepted, remove it from the dropzone to allow further valid requests.
              if (dropzone.current) dropzone.current.removeFile(file);
            }
          },
          removedfile() {
            updateTotalUploadProgress();
            maybeSignalQueueDrained();
          },
          accept: async (file: any, done) => {
            if (!dropzone.current) {
              console.error('Dropzone not initialized');
              return;
            }

            setUploadActive(true);
            setUploadInProgress?.(true);

            if (Number.isFinite(maxFiles) && dropzone.current.files.length > maxFiles) {
              setMaxFilesReached(true);
              skippedFilesRef.current += 1;
              dropzone.current.removeFile(file);
              return;
            }

            setTotalFiles((prev) => prev + 1);

            if (
              softWarnAfter !== undefined &&
              !softWarnFiredRef.current &&
              dropzone.current.files.length >= softWarnAfter
            ) {
              softWarnFiredRef.current = true;
              enqueueSnackbar(t('DropzoneComponent_SoftCapWarning', { count: dropzone.current.files.length }), {
                variant: 'warning',
                autoHideDuration: SnackbarTimeouts.Warning,
              });
            }

            if (Number.isFinite(maxFiles) && dropzone.current.files.length === maxFiles) {
              setMaxFilesReached(true);
            }

            if (!autoProcessQueue) {
              // Deferred mode: just accept the file, upload will be triggered manually
              onQueueChanged?.(dropzone.current.files.length);
              updateHandledFiles(file as Dropzone.DropzoneFile);
              done();
              return;
            }

            // Show every file in the list from the moment it is accepted (waiting → uploading →
            // done), in upload order, so the progress view reflects the whole pipeline.
            if (virtualizeRows) {
              updateHandledFiles(file as Dropzone.DropzoneFile);
            }

            // Admit the file without minting its upload URL yet. The presigned URL is created
            // on demand in `transformFile` — i.e. right when this file actually starts uploading
            // (throttled by `parallelUploads`) — so a short-lived URL can't expire while the file
            // waits its turn in the queue behind other uploads.
            done();
          },
          addedfiles: () => {
            if (skippedFilesRef.current > 0) {
              enqueueSnackbar(
                t('DropzoneComponent_MaxFilesExceeded', {
                  maxFiles,
                  skipped: skippedFilesRef.current,
                }),
                { variant: 'warning', autoHideDuration: SnackbarTimeouts.Warning },
              );
              skippedFilesRef.current = 0;
            }
          },
          url: (files) => {
            const file: any = files[0];
            return file.dynamicUploadUrl;
          },
          // Mint the presigned upload URL on demand, the moment Dropzone starts this file's
          // upload (this hook runs inside `uploadFiles`, gated by `parallelUploads`). The URL is
          // stashed per-file and read by the `url` function above — keeping it fresh when its PUT
          // runs instead of minting all URLs up-front in `accept` where the tail would expire.
          // Deferred mode (`autoProcessQueue: false`) mints via the host's `processAll`, so we
          // pass through here to avoid minting twice.
          transformFile(file: any, done: (file: File) => void) {
            if (!autoProcessQueue) {
              done(file);
              return;
            }
            createUploadUrl(file.name, file)
              .then((uploadedFile: UploadingFile) => {
                file.dynamicUploadUrl = uploadedFile.fileUrl;
                file.fileId = uploadedFile.fileId;
                done(file);
              })
              .catch(() => {
                // Mint failed — fail this file WITHOUT starting the PUT. Calling done() here would
                // let Dropzone PUT the body to a bogus URL (no `dynamicUploadUrl`), which behind a
                // CDN that serves 200 for unknown paths could even register as a false success
                // (silent data loss). Instead mark it errored and drive Dropzone's error/complete
                // flow ourselves, then free the queue slot — no bytes leave the browser.
                const dz = dropzone.current;
                if (!dz) return;
                file.uploadFailed = true;
                file.status = 'error' as Dropzone.DropzoneFile['status'];
                dz.emit('error', file, 'Failed to create upload URL');
                dz.emit('complete', file);
                dz.processQueue();
              });
          },
          ...(fileType !== FileType.ANY && {
            acceptedFiles: fileType,
          }),
          maxFilesize: 5242880,
          success(file: Dropzone.DropzoneFile & FileId) {
            onUploadSuccess?.(file);
            setSucceedCount((prevCount) => prevCount + 1);
            /*
             *  When the file is successfully uploaded, remove it from the dropzone to allow further valid requests.
             *  This is needed because after the file is uploaded, it stays in the queue, which prevents further uploads.
             * */
            if (dropzone.current) dropzone.current.removeFile(file);
          },
          previewsContainer: false,
          complete(file) {
            updateHandledFiles(file);
            updateTotalUploadProgress();
            maybeSignalQueueDrained();
          },
          sending(file, xhr) {
            const _send = xhr.send;
            xhr.send = () => {
              _send.call(xhr, file);
            };
          },
          parallelUploads,
          queuecomplete() {
            // Beware, this is actually not called when everything is done...
            setUploadActive(false);
            setUploadInProgress?.(false);
          },
        });
        setDropzoneInitialized(true);

        // Seed handed-in files now that the instance exists. Doing this here (rather than
        // from a host effect) removes the race where the host's effect runs before this
        // one and finds getDropzone() === null, silently dropping the files.
        if (initialFiles && initialFiles.length > 0) {
          for (const file of initialFiles) {
            dropzone.current.addFile(file as Dropzone.DropzoneFile);
          }
        }
      }
      return () => {
        if (dropzone.current) {
          dropzone.current.off();
          dropzone.current.destroy();
        }
      };
    }

    void uploadFiles();

    // eslint-disable-next-line
  }, [
    createUploadUrl,
    maxFiles,
    softWarnAfter,
    initialFiles,
    onUploadSuccess,
    fileType,
    enqueueSnackbar,
    dropzoneInitialized,
    setUploadInProgress,
    t,
    updateTotalUploadProgress,
    autoProcessQueue,
    parallelUploads,
    onQueueChanged,
    maybeSignalQueueDrained,
  ]);

  useEffect(() => {
    if (Number.isFinite(maxFiles) && succeedCount === maxFiles) {
      setUploadInProgress?.(false);
    }
  }, [succeedCount, maxFiles, setUploadInProgress]);

  useImperativeHandle(ref, () => ({
    getDropzone: () => dropzone.current,
    setMaxedReached: (maxedReached: boolean) => setMaxFilesReached(maxedReached),
    processAll: async (
      filesToProcess?: Dropzone.DropzoneFile[],
      onFileError?: (file: Dropzone.DropzoneFile) => void,
    ) => {
      const dz = dropzone.current;
      if (!dz) return [];
      const files = filesToProcess ?? [...dz.files];
      const failed: Dropzone.DropzoneFile[] = [];
      for (const file of files) {
        try {
          const uploadedFile = await createUploadUrl((file as any).name, file as any);
          (file as any).dynamicUploadUrl = uploadedFile.fileUrl;
          (file as any).fileId = uploadedFile.fileId;
          (file as any).uploadFailed = false;

          // Await the actual upload completion, not just the queue
          await new Promise<void>((resolve, reject) => {
            const onComplete = (completedFile: Dropzone.DropzoneFile) => {
              if (completedFile !== file) return;
              dz.off('complete', onComplete);
              if (completedFile.status === 'error') {
                reject(new Error('Upload failed'));
              } else {
                resolve();
              }
            };
            dz.on('complete', onComplete);
            try {
              dz.processFile(file);
            } catch (error) {
              dz.off('complete', onComplete);
              reject(error);
            }
          });
        } catch {
          (file as any).uploadFailed = true;
          failed.push(file);
          onFileError?.(file);
        }
      }
      if (failed.length > 0) {
        // Trigger re-render so DropzoneUploadProgress picks up the failed status
        setHandledFiles((prev) => [...prev]);
      }
      return failed;
    },
  }));

  if (isFloatingActionButton) {
    return (
      <Fab variant={'extended'} color={'default'} ref={floatingActionButtonNode} disabled={isDisabled} sx={sx}>
        {text}
      </Fab>
    );
  }

  return (
    <Stack>
      {title ? (
        <PTypography variant="h5" sx={{ pt: 0, pb: 4 }}>
          {title}
        </PTypography>
      ) : null}
      <Grid
        className="dropzone"
        ref={dropzoneNode}
        justifyContent="center"
        direction={'column'}
        sx={{
          border: `1px dashed ${alpha(theme.palette.grey[500], 0.2)}`,
          borderRadius: '8px',
          minHeight: '200px',
          backgroundColor: 'grey.200',
          cursor: 'pointer',
          alignItems: 'center',
          px: 4,
          ...(minimal && {
            minHeight: 'auto',
            py: 3,
            px: 3,
            alignItems: 'flex-start',
          }),
          ...((maxFilesReached || isDisabled) && {
            cursor: 'not-allowed',
            pointerEvents: 'none',
            opacity: 0.7,
          }),
          ...(hideDropArea && { display: 'none' }),
          ...gridProps?.sx,
        }}
        container
        item
      >
        <Stack
          direction={direction}
          alignItems="center"
          className="images-empty-messages-holder dz-message dz-default"
          sx={{ maxWidth: '500px', gap: '1rem', pointerEvents: 'none' }}
        >
          <Iconify
            icon={ICONS.FA_SOLID_FILE_UPLOAD}
            width={iconSize ? ICON_SIZE_MAP[iconSize] : 80}
            height={iconSize ? ICON_SIZE_MAP[iconSize] : 80}
            color={theme.palette.text.disabled}
            sx={{ pr: minimal ? 0 : 2 }}
          />
          <Stack>
            {fileDisplayName ? (
              <PTypography variant="h4">{t('General_UploadFileName', { fileName: fileDisplayName })}</PTypography>
            ) : null}

            {description}
            {!hideMaxFilesWarning && Number.isFinite(maxFiles) && (
              <PTypography variant="body1">{t('DropzoneComponent_MaxFileWarning', { count: maxFiles })}</PTypography>
            )}
          </Stack>
        </Stack>
      </Grid>
      {virtualizeRows ? (
        // Fixed-layout mode (bulk upload dialog): reserve the list + progress regions up
        // front from the stable total count so the modal height never grows as uploads
        // complete, and keep all scrolling inside the bounded list (no competing scroll
        // with the dialog that would slide rows under the dropzone).
        totalFiles > 0 &&
        !hideProgressBar && (
          <>
            <Divider sx={{ mt: 2 }} />
            <Box mr={-2} sx={{ mt: 2 }}>
              <VirtualizedRowList
                files={handledFiles}
                reservedCount={totalFiles}
                onRetry={onRetryFile}
                onRemove={!autoProcessQueue ? removeHandledFile : undefined}
              />
            </Box>
            {totalFiles > 1 ? (
              <Stack mt={2} spacing={0.5}>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                  <PTypography variant="subtitle1">
                    {uploadActive ? t('DropzoneComponent_Uploading') : t('DropzoneComponent_UploadFinished')}
                  </PTypography>
                  <PTypography variant="subtitle1">
                    {succeedCount}/{totalFiles}
                  </PTypography>
                </Stack>
                <LinearProgress variant="determinate" value={totalFiles > 0 ? (succeedCount / totalFiles) * 100 : 0} />
              </Stack>
            ) : null}
          </>
        )
      ) : (
        <>
          {handledFiles.length > 0 && !hideProgressBar && (
            <Box mr={-2}>
              <Scrollbar sx={{ maxHeight: 240 }}>
                {[...handledFiles].reverse().map((handledFile) => (
                  <DropzoneUploadProgress
                    key={handledFile.upload?.uuid ?? `${handledFile.name}-${handledFile.lastModified}`}
                    file={handledFile}
                    onRemove={!autoProcessQueue ? removeHandledFile : undefined}
                    onRetry={onRetryFile}
                  />
                ))}
              </Scrollbar>
            </Box>
          )}
          {totalUploadProgress > 0 && totalFiles > 1 && !hideProgressBar && (
            <Stack mt={2}>
              <Stack direction={'row'} justifyContent={'end'}>
                <PTypography variant="subtitle1">
                  {succeedCount}/{totalFiles}
                </PTypography>
              </Stack>
              <Stack>
                <LinearProgress variant="determinate" value={totalFiles > 0 ? (succeedCount / totalFiles) * 100 : 0} />
              </Stack>
            </Stack>
          )}
        </>
      )}
      {maxFilesReached && Number.isFinite(maxFiles) && maxFiles > 1 ? (
        <Alert severity="warning" sx={{ mx: 2, mt: 2 }}>
          {t('DropzoneComponent_MaxNumberOfFilesWarning')}
        </Alert>
      ) : null}

      {isDisabled && disabledMessage ? (
        <Alert severity="warning" sx={{ mx: 2, mt: 2 }}>
          {disabledMessage}
        </Alert>
      ) : null}
    </Stack>
  );
});

function DropzoneUploadProgress({
  file,
  onRemove,
  onRetry,
  disableMargin = false,
}: {
  file: Dropzone.DropzoneFile;
  onRemove?: (file: Dropzone.DropzoneFile) => void;
  onRetry?: (file: Dropzone.DropzoneFile) => void;
  disableMargin?: boolean;
}) {
  const theme = useTheme();
  const { t, localize } = useI18n();
  const fileStatus =
    (file as any).uploadFailed || file.status === 'error'
      ? 'failed'
      : file.upload && file.upload.progress >= 100
        ? 'done'
        : file.upload && file.upload.progress > 0
          ? 'progress'
          : 'waiting';
  return (
    <Box
      sx={{
        border: '1px solid rgba(57, 62, 67, 32%)',
        borderRadius: '8px',
        mr: 2,
        ...(disableMargin
          ? { height: ROW_CARD_HEIGHT, display: 'flex', alignItems: 'center', overflow: 'hidden' }
          : { my: 1 }),
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
        <Stack direction="row" sx={{ p: 1, pl: 2 }} alignItems="center" spacing={1}>
          <img
            src={getFileIcon(file.name)}
            alt=""
            width={ICON_SIZE_MAP.md}
            height={ICON_SIZE_MAP.md}
            style={{ objectFit: 'contain', flexShrink: 0 }}
          />
          <Stack width={350}>
            <Tooltip title={file.name}>
              <PTypography variant="subtitle2" noOverflow={{ maxWidth: 350, title: file.name }}>
                {file.name}
              </PTypography>
            </Tooltip>
            <PTypography variant="caption">{localize.formatAsDataSize(file.size)}</PTypography>
          </Stack>
        </Stack>
        <Stack direction="row" sx={{ p: disableMargin ? 1 : 2 }} alignItems="center" spacing={1}>
          {fileStatus === 'failed' ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              {onRetry ? (
                <Tooltip title={t('DropzoneComponent_RetryFile')}>
                  <IconButton size="small" onClick={() => onRetry(file)}>
                    <Iconify icon={ICONS.MDI_RELOAD} width={ICON_SIZE_MAP.smPlus} height={ICON_SIZE_MAP.smPlus} />
                  </IconButton>
                </Tooltip>
              ) : null}
              <Iconify
                icon={ICONS.ALERT_CIRCLE}
                width={ICON_SIZE_MAP.md}
                height={ICON_SIZE_MAP.md}
                color={theme.palette.error.main}
              />
            </Stack>
          ) : fileStatus === 'waiting' ? (
            onRemove ? (
              <IconButton size="small" onClick={() => onRemove(file)}>
                <Iconify icon={ICONS.CLOSE} width={ICON_SIZE_MAP.smPlus} height={ICON_SIZE_MAP.smPlus} />
              </IconButton>
            ) : (
              <Iconify icon={ICONS.MDI_TIMER_SAND_EMPTY} width={ICON_SIZE_MAP.md} height={ICON_SIZE_MAP.md} />
            )
          ) : fileStatus === 'progress' ? (
            <Stack>
              <CircularProgress
                variant="determinate"
                key={file.upload?.progress ?? 0}
                value={file.upload?.progress ?? 0}
                size={24}
                thickness={5}
              />
            </Stack>
          ) : (
            <Iconify
              icon={ICONS.CHECK_CIRCLE}
              width={ICON_SIZE_MAP.md}
              height={ICON_SIZE_MAP.md}
              color={theme.palette.success.main}
            />
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

const ROW_CARD_HEIGHT = 56;
const ROW_HEIGHT = ROW_CARD_HEIGHT + 8;
const MAX_VISIBLE_ROWS = 5;

function VirtualizedRowList({
  files,
  reservedCount,
  onRetry,
  onRemove,
}: {
  files: Dropzone.DropzoneFile[];
  /** Total accepted file count — used to reserve a stable height regardless of how many rows have completed yet. */
  reservedCount: number;
  onRetry?: (file: Dropzone.DropzoneFile) => void;
  onRemove?: (file: Dropzone.DropzoneFile) => void;
}) {
  const parentRef = useRef<HTMLDivElement | null>(null);
  const virtualizer = useVirtualizer({
    count: files.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 6,
  });

  const activeIndex = files.findIndex((f) => f.status !== 'error' && (f.upload?.progress ?? 0) < 100);
  useEffect(() => {
    if (activeIndex >= 0) {
      virtualizer.scrollToIndex(activeIndex, { align: 'center' });
    }
  }, [activeIndex, virtualizer]);

  // Reserve the height from the (stable) total count, capped — so the list, and therefore
  // the modal, keeps a constant size while rows stream in as uploads complete. Rows scroll
  // inside this fixed region; the dialog itself never scrolls.
  const visibleRows = Math.min(Math.max(reservedCount, files.length, 1), MAX_VISIBLE_ROWS);
  const containerHeight = visibleRows * ROW_HEIGHT;

  return (
    <Box ref={parentRef} sx={{ height: containerHeight, overflow: 'auto' }}>
      <Box sx={{ height: virtualizer.getTotalSize(), position: 'relative', width: '100%' }}>
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const file = files[virtualRow.index];
          return (
            <Box
              key={file.upload?.uuid ?? `${file.name}-${file.lastModified}`}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: ROW_HEIGHT,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <DropzoneUploadProgress file={file} onRetry={onRetry} onRemove={onRemove} disableMargin />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default DropzoneComponent;
